import axios from 'axios';
import type { BlastSportSummary, GlobalSearchResponse, SearchEntityType, PaginationMeta, OrgSportOption, FundraiserScreenshot } from '../types';
import type {
  CallQueue,
  CallQueueItem,
  CallQueueSummary,
  QueueFilters,
  DispositionPayload,
} from '../types';
import type { NamedRegion, CreateNamedRegionInput, UpdateNamedRegionInput } from '../types/namedRegion';
import type {
  SalesTeamSummary,
  SalesTeamDetail,
  CreateSalesTeamInput,
  UpdateSalesTeamInput,
  SetMembersInput,
} from '../types/salesTeam';
import type {
  LeadApproveResult,
  LeadDiscardReason,
  LeadQueueFilters,
  LeadSource,
  LeadSubmissionListItem,
  LeadSubmissionStatus,
  PublicLeadFormValues,
  PublicLeadSource,
} from '../types/lead';
import type { ReviewAnswer, ReviewDoc, ReviewNote } from '../types/review';
import type {
  Campaign,
  CampaignRecipient,
  CampaignRun,
  AttachmentSelector,
  AudiencePreview,
  ImageSet,
  EmailTemplate,
  TemplateSlots,
} from '../types/campaign';
import type { List, ListItemView, ListShare, FilterSpec, ListEntityKind, FavoriteIds, SortSpec } from '../types/list';
import type { AssignmentRequestListItem, AssignmentGrantScope, AssignmentRequestStatus, AssignmentRequestFilter } from '../types';
import type {
  CoachMoveReason,
  CoachMoveDiscoveryMethod,
  CoachMoveDecideAction,
  CoachMoveFlagItem,
  CoachMoveNotifyItem,
  TrustedRep,
  PersonMergeStatus,
  PersonMergeDecideAction,
  PersonMergeItem,
  MergePreview,
} from '../types';

// Create axios instance
// In development, Vite proxy handles /api and /dev routes
// In production, we'd use VITE_API_URL
const api = axios.create({
  baseURL: '',  // Empty because Vite proxy handles it
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage — localStorage so it's shared across tabs of the same origin.
// This is what makes right-click → "Open in New Tab" work without bouncing
// the user through Blast SSO again. Token expiry is enforced server-side by
// the JWT itself; the 401 response interceptor below clears it on rejection.
const TOKEN_KEY = 'crm_auth_token';

let authToken: string | null = localStorage.getItem(TOKEN_KEY);

/**
 * Set the auth token for API requests
 */
export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};

/**
 * Get the current auth token
 */
export const getAuthToken = (): string | null => {
  return authToken;
};

/**
 * Clear the auth token
 */
export const clearAuthToken = () => {
  authToken = null;
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Decide whether a 401 response should drop the session.
 *
 * Only clear the token when the request that failed was actually carrying the
 * CURRENT live token — i.e. the live token itself was rejected (genuine expiry
 * or invalidation). A 401 from a request that went out with a stale token, or
 * with no token at all (e.g. a data call that raced ahead of session restore),
 * must NOT wipe a freshly-restored, still-valid token. Wiping it was the root
 * cause of the "logged in but every API call 401s" cascade: one stray 401
 * cleared the live token (and localStorage with it), 401-ing everything after.
 */
export const shouldClearTokenOn401 = (
  sentAuthHeader: string | undefined,
  liveToken: string | null,
): boolean => {
  if (!liveToken) return false;
  const sent =
    typeof sentAuthHeader === 'string' && sentAuthHeader.startsWith('Bearer ')
      ? sentAuthHeader.slice(7)
      : null;
  return sent === liveToken;
};

// Add request interceptor to include auth token. Read the freshest token —
// in-memory first, falling back to localStorage — so a request that fires while
// `authToken` is transiently null (mid session-restore) still goes out
// authenticated instead of racing out without a Bearer header.
api.interceptors.request.use(
  (config) => {
    const token = authToken ?? localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling. On a 401 we only drop the
// session if the CURRENT live token was the one rejected (see
// shouldClearTokenOn401) — stray 401s from raced or stale-token requests must
// not wipe a still-valid token.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const liveToken = authToken ?? localStorage.getItem(TOKEN_KEY);
      const sentAuthRaw =
        error.config?.headers?.Authorization ?? error.config?.headers?.authorization;
      const sentAuth = typeof sentAuthRaw === 'string' ? sentAuthRaw : undefined;
      if (shouldClearTokenOn401(sentAuth, liveToken)) {
        // The live token itself was rejected (expired/invalid) — clear it so the
        // next navigation bounces through Sign In.
        console.error('Authentication error (live token rejected):', error.response?.data);
        clearAuthToken();
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/**
 * Sports belonging to one organization — powers the (optional) sport picker
 * in AddFundraiserModal's no-context mode. There is no global sport list:
 * sports live under an organization, and the Blast integration sport
 * vocabulary (integrationApi.getBlastSports) is a different, unrelated table.
 */
export const getOrganizationSports = async (orgId: string): Promise<OrgSportOption[]> => {
  const res = await api.get<{ sports: OrgSportOption[] }>(`/api/organizations/${orgId}/sports`);
  return res.data.sports;
};

export const fundraiserScreenshotsApi = {
  list: async (fundraiserId: string): Promise<FundraiserScreenshot[]> => {
    const res = await api.get<{ screenshots: FundraiserScreenshot[] }>(`/api/fundraisers/${fundraiserId}/screenshots`);
    return res.data.screenshots;
  },
  /** Sends one image per request, in order, so each request body stays within a single image's size limit. */
  upload: async (fundraiserId: string, files: Blob[]): Promise<FundraiserScreenshot[]> => {
    const saved: FundraiserScreenshot[] = [];
    for (const [i, f] of files.entries()) {
      const form = new FormData();
      form.append('files', f, f instanceof File ? f.name : `screenshot-${i + 1}.jpg`);
      const res = await api.post<{ screenshots: FundraiserScreenshot[] }>(
        `/api/fundraisers/${fundraiserId}/screenshots`, form,
        // The shared instance defaults to application/json, which makes axios serialize
        // FormData to JSON and drop the file (axios lib/defaults/index.js). Declaring
        // multipart keeps the body intact; axios then lets the browser set the boundary.
        { headers: { 'Content-Type': 'multipart/form-data' } });
      saved.push(...res.data.screenshots);
    }
    return saved;
  },
  remove: async (fundraiserId: string, screenshotId: string): Promise<void> => {
    await api.delete(`/api/fundraisers/${fundraiserId}/screenshots/${screenshotId}`);
  },
};

// Blast integration helpers
export const integrationApi = {
  // GET /api/integration/sports — returns the BlastSport array directly (not wrapped)
  getBlastSports: async (): Promise<BlastSportSummary[]> => {
    const res = await api.get<BlastSportSummary[]>('/api/integration/sports');
    return res.data;
  },
};

export const searchApi = {
  /**
   * Fan-out search across people/organizations/tasks.
   *
   * @param q       Query string. Caller should ensure ≥ 2 chars before calling.
   * @param options Optional types and per-type limit.
   * @param signal  AbortSignal so callers can cancel stale requests.
   */
  async search(
    q: string,
    options: { types?: SearchEntityType[]; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<GlobalSearchResponse> {
    const params: Record<string, string | number> = { q };
    if (options.types) params.types = options.types.join(',');
    if (options.limit) params.limit = options.limit;

    const res = await api.get<GlobalSearchResponse>('/api/search', { params, signal });
    return res.data;
  },
};

// Sport season defaults (admin-facing matrix CRUD)
export const sportSeasonDefaultsApi = {
  async matrix() {
    const res = await api.get('/api/sport-season-defaults/matrix');
    return res.data.matrix;
  },
  async list(params: Record<string, unknown>) {
    const res = await api.get('/api/sport-season-defaults', { params });
    return res.data;
  },
  async coverage() {
    const res = await api.get('/api/sport-season-defaults/coverage');
    return res.data;
  },
  async create(body: unknown) {
    const res = await api.post('/api/sport-season-defaults', body);
    return res.data.default;
  },
  async update(id: string, body: unknown, dryRun?: boolean) {
    const url = `/api/sport-season-defaults/${id}${dryRun ? '?dryRun=true' : ''}`;
    const res = await api.patch(url, body);
    return res.data;
  },
  async remove(id: string) {
    await api.delete(`/api/sport-season-defaults/${id}`);
  },
  async importPreview(csv: string) {
    const res = await api.post('/api/sport-season-defaults/import/preview', { csv });
    return res.data.diff;
  },
  async importApply(csv: string) {
    const res = await api.post('/api/sport-season-defaults/import/apply', { csv });
    return res.data;
  },
  async export() {
    const res = await api.get('/api/sport-season-defaults/export', { responseType: 'blob' });
    return res.data;
  },
};

// Named regions (admin-facing CRUD on intra-state ZIP3 groupings)
export const namedRegionsApi = {
  list(params?: { stateCode?: string }): Promise<{ namedRegions: NamedRegion[] }> {
    return api.get('/api/named-regions', { params }).then((r) => r.data);
  },
  get(id: string): Promise<{ namedRegion: NamedRegion }> {
    return api.get(`/api/named-regions/${id}`).then((r) => r.data);
  },
  create(input: CreateNamedRegionInput): Promise<{ namedRegion: NamedRegion }> {
    return api.post('/api/named-regions', input).then((r) => r.data);
  },
  update(id: string, input: UpdateNamedRegionInput): Promise<{ namedRegion: NamedRegion }> {
    return api.put(`/api/named-regions/${id}`, input).then((r) => r.data);
  },
  delete(id: string): Promise<void> {
    return api.delete(`/api/named-regions/${id}`).then(() => undefined);
  },
};

// Sales teams (manager-facing list/mine; admin-facing CRUD)
export const salesTeamsApi = {
  list(): Promise<{ salesTeams: SalesTeamSummary[] }> {
    return api.get('/api/sales-teams').then((r) => r.data);
  },
  mine(): Promise<{ salesTeams: SalesTeamSummary[] }> {
    return api.get('/api/sales-teams/mine').then((r) => r.data);
  },
  get(id: string): Promise<{ salesTeam: SalesTeamDetail }> {
    return api.get(`/api/sales-teams/${id}`).then((r) => r.data);
  },
  create(input: CreateSalesTeamInput): Promise<{ salesTeam: SalesTeamDetail }> {
    return api.post('/api/sales-teams', input).then((r) => r.data);
  },
  update(id: string, input: UpdateSalesTeamInput): Promise<{ salesTeam: SalesTeamDetail }> {
    return api.patch(`/api/sales-teams/${id}`, input).then((r) => r.data);
  },
  remove(id: string): Promise<void> {
    return api.delete(`/api/sales-teams/${id}`).then(() => undefined);
  },
  setMembers(id: string, input: SetMembersInput): Promise<void> {
    return api.put(`/api/sales-teams/${id}/members`, input).then(() => undefined);
  },
};

// Assignment requests (rep-facing create/mine; manager/admin-facing queue + decisions)
export const assignmentRequestsApi = {
  listQueue(status: AssignmentRequestFilter = 'PENDING'): Promise<{ requests: AssignmentRequestListItem[] }> {
    return api.get(`/api/assignment-requests?view=queue&status=${status}`).then((r) => r.data);
  },
  listMine(status: AssignmentRequestFilter = 'PENDING'): Promise<{ requests: AssignmentRequestListItem[] }> {
    return api.get(`/api/assignment-requests?view=mine&status=${status}`).then((r) => r.data);
  },
  count(status: AssignmentRequestStatus | 'OPEN' = 'PENDING', view: 'queue' | 'mine' = 'queue'): Promise<{ count: number }> {
    return api.get(`/api/assignment-requests/count?status=${status}&view=${view}`).then((r) => r.data);
  },
  create(personId: string, note?: string): Promise<{ request: { id: string; status: AssignmentRequestStatus } }> {
    return api.post('/api/assignment-requests', { personId, note }).then((r) => r.data);
  },
  approve(id: string, grantScope: AssignmentGrantScope): Promise<{ request: AssignmentRequestListItem }> {
    return api.post(`/api/assignment-requests/${id}/approve`, { grantScope }).then((r) => r.data);
  },
  deny(id: string, note?: string): Promise<{ request: AssignmentRequestListItem }> {
    return api.post(`/api/assignment-requests/${id}/deny`, { note }).then((r) => r.data);
  },
  hold(id: string, note?: string): Promise<{ request: AssignmentRequestListItem }> {
    return api.post(`/api/assignment-requests/${id}/hold`, { note }).then((r) => r.data);
  },
  cancel(id: string): Promise<{ request: AssignmentRequestListItem }> {
    return api.post(`/api/assignment-requests/${id}/cancel`, {}).then((r) => r.data);
  },
};

// Coach move flags (rep-facing raise; matching-support/data-steward-facing queue + decisions)
export const coachMoveFlagsApi = {
  raise(body: {
    personId: string; fromOrganizationId: string; reason: CoachMoveReason;
    discoveryMethod: CoachMoveDiscoveryMethod; toOrganizationId?: string | null;
    toSchoolText?: string | null; note?: string | null;
  }): Promise<{ flag: CoachMoveFlagItem }> {
    return api.post('/api/coach-move-flags', body).then((r) => r.data);
  },
  notify(body: {
    personId: string; fromOrganizationId: string; reason: CoachMoveReason;
    discoveryMethod: CoachMoveDiscoveryMethod; toOrganizationId?: string | null;
    toSchoolText?: string | null; note?: string | null;
  }): Promise<{ task: CoachMoveNotifyItem }> {
    return api.post('/api/coach-move-flags/notify', body).then((r) => r.data);
  },
  list(view: 'queue' | 'call-list' | 'mine' | 'decided'): Promise<{ flags: CoachMoveFlagItem[] }> {
    return api.get(`/api/coach-move-flags?view=${view}`).then((r) => r.data);
  },
  count(view: 'queue' | 'call-list' | 'mine' | 'decided'): Promise<{ count: number }> {
    return api.get(`/api/coach-move-flags/count?view=${view}`).then((r) => r.data);
  },
  forPerson(personId: string): Promise<{ flags: CoachMoveFlagItem[]; openNotify: CoachMoveNotifyItem | null }> {
    return api.get(`/api/coach-move-flags/person/${personId}`).then((r) => r.data);
  },
  confirm(id: string, verifyMethod: CoachMoveDiscoveryMethod): Promise<{ flag: CoachMoveFlagItem }> {
    return api.post(`/api/coach-move-flags/${id}/confirm`, { verifyMethod }).then((r) => r.data);
  },
  decide(id: string, body: { action: CoachMoveDecideAction; toOrganizationId?: string; note?: string }): Promise<{ flag: CoachMoveFlagItem }> {
    return api.post(`/api/coach-move-flags/${id}/decide`, body).then((r) => r.data);
  },
  revert(id: string): Promise<{ flag: CoachMoveFlagItem }> {
    return api.post(`/api/coach-move-flags/${id}/revert`, {}).then((r) => r.data);
  },
  trustedReps(): Promise<{ users: TrustedRep[] }> {
    return api.get('/api/coach-move-flags/trusted-reps').then((r) => r.data);
  },
  setTrusted(userId: string, trusted: boolean): Promise<{ user: TrustedRep }> {
    return api.put(`/api/coach-move-flags/trusted-reps/${userId}`, { trusted }).then((r) => r.data);
  },
};

export const personMergesApi = {
  raise(body: { survivorId: string; loserId: string; reason?: string | null }): Promise<{ merge: PersonMergeItem }> {
    return api.post('/api/person-merges', body).then((r) => r.data);
  },
  preview(survivorId: string, loserId: string): Promise<{ preview: MergePreview }> {
    return api.get(`/api/person-merges/preview?survivorId=${encodeURIComponent(survivorId)}&loserId=${encodeURIComponent(loserId)}`).then((r) => r.data);
  },
  list(params: { status?: PersonMergeStatus; personId?: string } = {}): Promise<{ merges: PersonMergeItem[]; pagination: { total: number } }> {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.personId) q.set('personId', params.personId);
    return api.get(`/api/person-merges?${q.toString()}`).then((r) => r.data);
  },
  decide(id: string, body: { action: PersonMergeDecideAction; note?: string }): Promise<{ merge: PersonMergeItem }> {
    return api.post(`/api/person-merges/${id}/decide`, body).then((r) => r.data);
  },
  revert(id: string): Promise<{ merge: PersonMergeItem }> {
    return api.post(`/api/person-merges/${id}/revert`, {}).then((r) => r.data);
  },
};

// Sport season override (rep-facing PATCH on a single Sport)
export const sportSeasonOverrideApi = {
  async override(orgId: string, sportId: string, season: string) {
    const res = await api.patch(`/api/organizations/${orgId}/sports/${sportId}/season`, {
      season,
      isOverride: true,
    });
    return res.data.sport;
  },
  async reset(orgId: string, sportId: string) {
    const res = await api.patch(`/api/organizations/${orgId}/sports/${sportId}/season`, {
      reset: true,
    });
    return res.data.sport;
  },
};

export const callQueueApi = {
  list: async (): Promise<{ callQueues: CallQueue[] }> => {
    const res = await api.get('/api/call-queues');
    return res.data;
  },
  get: async (id: string): Promise<{ callQueue: CallQueue; summary: CallQueueSummary }> => {
    const res = await api.get(`/api/call-queues/${id}`);
    return res.data;
  },
  create: async (input: { name: string; filtersJson: QueueFilters }): Promise<{ callQueue: CallQueue }> => {
    const res = await api.post('/api/call-queues', input);
    return res.data;
  },
  update: async (id: string, input: Partial<{ name: string; filtersJson: QueueFilters }>): Promise<{ callQueue: CallQueue }> => {
    const res = await api.patch(`/api/call-queues/${id}`, input);
    return res.data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/api/call-queues/${id}`);
  },
  listItems: async (
    id: string,
    params: { page?: number; limit?: number; status?: string } = {}
  ): Promise<{ items: CallQueueItem[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> => {
    const res = await api.get(`/api/call-queues/${id}/items`, { params });
    return res.data;
  },
  next: async (id: string): Promise<{ item: CallQueueItem | null }> => {
    const res = await api.get(`/api/call-queues/${id}/next`);
    return res.data;
  },
  refresh: async (id: string): Promise<{ addedCount: number }> => {
    const res = await api.post(`/api/call-queues/${id}/refresh`, {});
    return res.data;
  },
  skip: async (id: string, personId: string): Promise<void> => {
    await api.post(`/api/call-queues/${id}/items/${personId}/skip`, {});
  },
  disposition: async (id: string, personId: string, payload: DispositionPayload): Promise<{ activity: any; item: CallQueueItem }> => {
    const res = await api.post(`/api/call-queues/${id}/items/${personId}/disposition`, payload);
    return res.data;
  },
};

export const dashboardInsideSalesApi = {
  daily: async (): Promise<{ self?: number; leaderboard?: Array<{ userId: string; count: number; user: { id: string; displayName: string | null; email: string; role: string } }> }> => {
    const res = await api.get('/api/dashboard/inside-sales-daily');
    return res.data;
  },
};

export const listsApi = {
  list: (scope: 'mine' | 'shared' | 'team' = 'mine', page = 1, limit = 50): Promise<{ lists: List[]; pagination: PaginationMeta }> =>
    api.get('/api/lists', { params: { scope, page, limit } }).then((r) => r.data),
  get: (id: string): Promise<{ list: List }> => api.get(`/api/lists/${id}`).then((r) => r.data),
  create: (input: { name: string; description?: string | null; color?: string | null; kind?: 'MANUAL' | 'SMART'; smartEntityKind?: ListEntityKind; filterSpec?: FilterSpec }): Promise<{ list: List }> =>
    api.post('/api/lists', input).then((r) => r.data),
  update: (id: string, input: Partial<{ name: string; description: string | null; color: string | null; filterSpec: FilterSpec; sortField: string | null; sortDir: 'asc' | 'desc' | null }>): Promise<{ list: List }> =>
    api.patch(`/api/lists/${id}`, input).then((r) => r.data),
  remove: (id: string): Promise<void> => api.delete(`/api/lists/${id}`).then(() => undefined),
  items: (id: string, params: { page?: number; limit?: number; type?: ListEntityKind; search?: string } = {}): Promise<{ items: ListItemView[]; pagination: PaginationMeta }> =>
    api.get(`/api/lists/${id}/items`, { params }).then((r) => r.data),
  addItems: (id: string, items: { kind: ListEntityKind; id: string }[]): Promise<{ added: number; items: unknown[] }> =>
    api.post(`/api/lists/${id}/items`, { items }).then((r) => r.data),
  removeItem: (id: string, itemId: string): Promise<void> => api.delete(`/api/lists/${id}/items/${itemId}`).then(() => undefined),
  bulkRemove: (id: string, itemIds: string[]): Promise<{ removed: number }> =>
    api.post(`/api/lists/${id}/items/bulk-delete`, { itemIds }).then((r) => r.data),
  reorder: (id: string, orderedItemIds: string[]): Promise<void> =>
    api.patch(`/api/lists/${id}/items/reorder`, { orderedItemIds }).then(() => undefined),
  sortItems: (id: string, field: string, dir: 'asc' | 'desc'): Promise<void> =>
    api.post(`/api/lists/${id}/items/sort-by`, { field, dir }).then(() => undefined),
  shares: (id: string): Promise<{ shares: ListShare[] }> => api.get(`/api/lists/${id}/shares`).then((r) => r.data),
  createShare: (id: string, input: { granteeType: 'USER' | 'TEAM'; granteeUserId?: string | null; permission: 'VIEW' | 'EDIT' }): Promise<{ share: ListShare }> =>
    api.post(`/api/lists/${id}/shares`, input).then((r) => r.data),
  deleteShare: (id: string, shareId: string): Promise<void> =>
    api.delete(`/api/lists/${id}/shares/${shareId}`).then(() => undefined),
  snapshot: (id: string): Promise<{ list: List; copied: number }> =>
    api.post(`/api/lists/${id}/snapshot`).then((r) => r.data),
  preview: (
    kind: ListEntityKind,
    filterSpec: FilterSpec,
    opts?: { page?: number; limit?: number; sort?: SortSpec },
  ): Promise<{ items: ListItemView[]; pagination: PaginationMeta }> =>
    api.post('/api/lists/preview', {
      kind,
      filterSpec,
      page: opts?.page ?? 1,
      limit: opts?.limit ?? 25,
      ...(opts?.sort ? { sort: opts.sort } : {}),
    }).then((r) => r.data),
  addFromFilter: (id: string, kind: ListEntityKind, filterSpec: FilterSpec): Promise<{ added: number }> =>
    api.post(`/api/lists/${id}/items/from-filter`, { kind, filterSpec }).then((r) => r.data),
};

export const favoritesApi = {
  get: (): Promise<{ list: List }> => api.get('/api/lists/favorites').then((r) => r.data),
  ids: (): Promise<FavoriteIds> => api.get('/api/lists/favorites/ids').then((r) => r.data),
  toggle: (kind: ListEntityKind, id: string): Promise<{ isFavorite: boolean }> =>
    api.post('/api/lists/favorites/toggle', { kind, id }).then((r) => r.data),
};

export const tagsApi = {
  list: (q?: string) => api.get('/api/tags', { params: q ? { q } : {} }).then((r) => r.data.tags),
  forRecord: (kind: string, entityId: string) =>
    api.get('/api/tags/for', { params: { kind, entityId } }).then((r) => r.data.tags),
  create: (body: { name: string; code?: string; color?: string | null; description?: string | null; isShared?: boolean }) =>
    api.post('/api/tags', body).then((r) => r.data.tag),
  apply: (id: string, kind: string, entityId: string) =>
    api.post(`/api/tags/${id}/apply`, { kind, entityId }).then((r) => r.data),
  remove: (id: string, kind: string, entityId: string) =>
    api.delete(`/api/tags/${id}/apply`, { data: { kind, entityId } }).then((r) => r.data),
  update: (id: string, body: Partial<{ name: string; code: string; color: string | null; description: string | null }>) =>
    api.patch(`/api/tags/${id}`, body).then((r) => r.data.tag),
  retire: (id: string) => api.delete(`/api/tags/${id}`).then((r) => r.data),
  promote: (id: string) => api.post(`/api/tags/${id}/promote`).then((r) => r.data.tag),
};

/**
 * Public lead capture (CRM-129) — unauthenticated. Called from /lead and
 * /lead/:repId, which anonymous visitors reach; the response is deliberately
 * contentless so nothing about reps or dedup leaks to the public.
 */
export const publicLeadsApi = {
  sources: (): Promise<{ leadSources: PublicLeadSource[] }> =>
    api.get('/api/public/lead-sources').then((r) => r.data),
  submit: (
    body: Partial<PublicLeadFormValues>,
    repId?: string,
  ): Promise<{ accepted: boolean; noteToken: string }> =>
    api.post('/api/public/lead-submissions', body, { params: repId ? { repId } : {} }).then((r) => r.data),
  /** Attaches the optional note from the thanks screen; the token is good once. */
  note: (token: string, note: string): Promise<{ saved: boolean }> =>
    api.post('/api/public/lead-submissions/note', { token, note }).then((r) => r.data),
};

export const leadSubmissionsApi = {
  list: (status: LeadSubmissionStatus = 'PENDING_REVIEW', filters: LeadQueueFilters = {}) =>
    api
      .get<{ leadSubmissions: LeadSubmissionListItem[] }>('/api/lead-submissions', {
        params: { status, ...filters },
      })
      .then((r) => r.data),
  count: (): Promise<{ count: number }> => api.get('/api/lead-submissions/count').then((r) => r.data),
  approve: (
    id: string,
    body: { assignToUserId?: string | null; sourceId?: string | null } = {},
  ): Promise<{ leadSubmission: LeadApproveResult }> =>
    api.post(`/api/lead-submissions/${id}/approve`, body).then((r) => r.data),
  discard: (id: string, reason: LeadDiscardReason): Promise<{ leadSubmission: { submissionId: string } }> =>
    api.post(`/api/lead-submissions/${id}/discard`, { reason }).then((r) => r.data),
};

export const leadSourcesApi = {
  list: (includeInactive = false): Promise<{ leadSources: LeadSource[] }> =>
    api.get('/api/lead-sources', { params: { includeInactive } }).then((r) => r.data),
  create: (label: string): Promise<{ leadSource: LeadSource }> =>
    api.post('/api/lead-sources', { label }).then((r) => r.data),
  update: (id: string, body: { label?: string; isActive?: boolean }): Promise<{ leadSource: LeadSource }> =>
    api.patch(`/api/lead-sources/${id}`, body).then((r) => r.data),
};

export const reviewsApi = {
  get: (slug: string): Promise<{ review: ReviewDoc }> =>
    api.get(`/api/reviews/${slug}`).then((r) => r.data),
  saveAnswer: (
    slug: string,
    questionKey: string,
    body: { choice?: string; comment?: string }
  ): Promise<{ answer: ReviewAnswer }> =>
    api.put(`/api/reviews/${slug}/answers/${questionKey}`, body).then((r) => r.data),
  addNote: (slug: string, text: string): Promise<{ note: ReviewNote }> =>
    api.post(`/api/reviews/${slug}/notes`, { text }).then((r) => r.data),
};

export const emailTemplatesApi = {
  list: async (): Promise<EmailTemplate[]> => (await api.get<{ templates: EmailTemplate[] }>('/api/email-templates')).data.templates,
  create: async (body: Partial<EmailTemplate>): Promise<EmailTemplate> => (await api.post<{ template: EmailTemplate }>('/api/email-templates', body)).data.template,
  update: async (id: string, body: Partial<EmailTemplate>): Promise<EmailTemplate> => (await api.patch<{ template: EmailTemplate }>(`/api/email-templates/${id}`, body)).data.template,
  preview: async (body: { subject: string; slots: TemplateSlots }) => (await api.post<{ preview: { subject: string; slots: Record<string, string | undefined>; missing: string[] } }>('/api/email-templates/preview', body)).data.preview,
};

export const campaignsApi = {
  list: async (): Promise<Campaign[]> => (await api.get<{ campaigns: Campaign[] }>('/api/campaigns')).data.campaigns,
  get: async (id: string): Promise<Campaign> => (await api.get<{ campaign: Campaign }>(`/api/campaigns/${id}`)).data.campaign,
  create: async (body: Record<string, unknown>): Promise<Campaign> => (await api.post<{ campaign: Campaign }>('/api/campaigns', body)).data.campaign,
  update: async (id: string, body: Record<string, unknown>): Promise<Campaign> => (await api.patch<{ campaign: Campaign }>(`/api/campaigns/${id}`, body)).data.campaign,
  listImageSets: async (): Promise<ImageSet[]> =>
    (await api.get<{ imageSets: ImageSet[] }>('/api/campaigns/image-sets')).data.imageSets,
  previewAudience: async (listId: string, attachmentSelector: AttachmentSelector, imageSetKey?: string | null): Promise<AudiencePreview> =>
    (await api.post<{ preview: AudiencePreview }>('/api/campaigns/preview-audience', { listId, attachmentSelector, ...(imageSetKey ? { imageSetKey } : {}) })).data.preview,
  testSend: async (id: string): Promise<CampaignRun> => (await api.post<{ run: CampaignRun }>(`/api/campaigns/${id}/test-send`, {})).data.run,
  sendNow: async (id: string, confirmOverLimit = false, confirmRunId?: string): Promise<CampaignRun> =>
    (await api.post<{ run: CampaignRun }>(`/api/campaigns/${id}/send-now`, { confirmOverLimit, ...(confirmRunId ? { confirmRunId } : {}) })).data.run,
  cancel: async (id: string): Promise<Campaign> => (await api.post<{ campaign: Campaign }>(`/api/campaigns/${id}/cancel`, {})).data.campaign,
  runs: async (id: string): Promise<CampaignRun[]> => (await api.get<{ runs: CampaignRun[] }>(`/api/campaigns/${id}/runs`)).data.runs,
  runDetail: async (id: string, runId: string, params: { status?: string; search?: string; page?: number; limit?: number } = {}) =>
    (await api.get<{ run: CampaignRun; recipients: CampaignRecipient[]; pagination: PaginationMeta }>(`/api/campaigns/${id}/runs/${runId}`, { params })).data,
  resume: async (id: string, runId: string, confirmOverLimit = false): Promise<CampaignRun> =>
    (await api.post<{ run: CampaignRun }>(`/api/campaigns/${id}/runs/${runId}/resume`, { confirmOverLimit })).data.run,
};
